import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useLayoutEffect} from "react";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {cancelSubscription, pauseSubscription} from "@/api/billing";
import {ApiError} from "@/api/client";
import {useCabinetData} from "@/hooks/useCabinetData";

type CancellationState = {
    reason?: string;
};

export function CabinetSubscriptionFreezePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const {subscription, paid, isLoading, isError, refetch} = useCabinetData();
    const cancellationReason = (location.state as CancellationState | null)?.reason?.trim();

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, []);

    const cancelMutation = useMutation({
        mutationFn: () => cancelSubscription(cancellationReason ?? ""),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["cabinet"]});
            navigate("/cabinet/subscription/cancelled", {replace: true});
        },
    });

    const pauseMutation = useMutation({
        mutationFn: () => pauseSubscription(cancellationReason ?? ""),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["cabinet"]});
            navigate("/cabinet/subscription/paused", {replace: true});
        },
    });

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Готовим варианты</h2>
                <p>Проверяем статус вашей подписки.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подписку</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (!paid || !subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    if (subscription.cancel_at_period_end) {
        return <Navigate to="/cabinet/subscription/cancelled" replace/>;
    }

    const cancelErrorMessage = cancelMutation.error instanceof ApiError
        ? cancelMutation.error.message
        : "Не удалось отключиться от Leadhold. Попробуйте ещё раз.";
    const pauseErrorMessage = pauseMutation.error instanceof ApiError
        ? pauseMutation.error.message
        : "Не удалось приостановить подписку. Попробуйте ещё раз.";

    return (
        <section className="cabinet-freeze-flow" aria-labelledby="freeze-title">
            <button
                type="button"
                className="cabinet-cancel-back"
                onClick={() => navigate("/cabinet/subscription/cancel", {
                    state: cancellationReason ? {reason: cancellationReason} : undefined,
                })}
            >
                <span aria-hidden="true">←</span>
                Назад
            </button>

            <article className="cabinet-panel cabinet-freeze-card">
                <div className="cabinet-freeze-hero" aria-hidden="true">
                    <div className="cabinet-freeze-orbit cabinet-freeze-orbit-one"/>
                    <div className="cabinet-freeze-orbit cabinet-freeze-orbit-two"/>
                    <div className="cabinet-freeze-calendar">
                        <span>30</span>
                        <small>дней</small>
                    </div>
                    <div className="cabinet-freeze-pause"><span/><span/></div>
                </div>

                <div className="cabinet-freeze-copy">
                    <span className="cabinet-cancel-eyebrow">Пауза вместо отмены</span>
                    <h2 id="freeze-title">Приостановить подписку на 30 дней?</h2>
                    <p>
                        Сделайте перерыв без окончательной отмены. Во время паузы
                        новых списаний не будет, а рабочие настройки останутся в аккаунте.
                    </p>
                </div>

                <button
                    type="button"
                    className="btn btn-primary cabinet-freeze-primary"
                    disabled={pauseMutation.isPending || cancelMutation.isPending || !cancellationReason}
                    aria-busy={pauseMutation.isPending}
                    onClick={() => pauseMutation.mutate()}
                >
                    {pauseMutation.isPending ? "Приостанавливаем..." : "Приостановить подписку"}
                </button>

                {pauseMutation.isError ? (
                    <p className="cabinet-message error" role="alert">{pauseErrorMessage}</p>
                ) : null}

                <div className="cabinet-freeze-benefits" aria-label="Условия паузы">
                    <div>
                        <span aria-hidden="true">01</span>
                        <strong>Без списаний</strong>
                        <p>Следующая оплата перенесётся на 30 дней.</p>
                    </div>
                    <div>
                        <span aria-hidden="true">02</span>
                        <strong>Всё сохранится</strong>
                        <p>Настройки, кампании и история останутся в аккаунте.</p>
                    </div>
                    <div>
                        <span aria-hidden="true">03</span>
                        <strong>Можно вернуться раньше</strong>
                        <p>Возобновите подписку в любой момент из личного кабинета.</p>
                    </div>
                </div>

                <div className="cabinet-freeze-secondary">
                    {cancelMutation.isError ? (
                        <p className="cabinet-message error" role="alert">{cancelErrorMessage}</p>
                    ) : null}
                    <button
                        type="button"
                        className="btn cabinet-cancel-confirm"
                        disabled={cancelMutation.isPending || pauseMutation.isPending || !cancellationReason}
                        aria-busy={cancelMutation.isPending}
                        onClick={() => cancelMutation.mutate()}
                    >
                        {cancelMutation.isPending ? "Отключаем..." : "Отключиться от Leadhold"}
                    </button>
                </div>
            </article>
        </section>
    );
}
